/* eslint-disable @typescript-eslint/no-explicit-any */
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { FormFieldTypes, type FormFieldType } from '@hero/web-types';
import { Checkbox } from '../../ui/Checkbox/Checkbox';
import { FormControl, FormField, FormItem, FormMessage, useFormField } from '../../ui/Form/Form';
import { Input } from '../../ui/Input/Input';
import { InputLabel } from '../../ui/Input';

import { format } from 'date-fns';
import { E164Number } from 'libphonenumber-js/core';
import { CalendarIcon } from 'lucide-react';
import OtpInput from 'react-otp-input';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { cn } from '@hero/web-lib';
import { Button } from '../../ui/Button/Button';
import { Calendar } from '../../ui/Calendar/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/Popover/Popover';
import { Select, SelectContent, SelectTrigger, SelectValue } from '../../ui/Select/Select';
import { Textarea } from '../../ui/Textarea/Textarea';
import './phone-input.css';

export interface CustomFormFieldProps<TFieldValues extends FieldValues = FieldValues> {
  className?: string;
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  filedType: FormFieldType;
  label?: string;
  /** When true, shows a required indicator (asterisk) next to the label. */
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  dateFormat?: string;
  showTimeSelect?: boolean;
  children?: React.ReactNode;
  renderSkeleton?: (field: any) => React.ReactNode;
  value?: string;
  type?: string;
  transform?: (value: any) => any;
  /**
   * Extra blur handler, composed AFTER react-hook-form's own `onBlur`
   * (which tracks touched state) so validation still fires. Used for
   * on-blur side effects like resolving a pasted URL. INPUT only.
   */
  onBlur?: () => void;
}
/** Normalize phone to E.164 for react-phone-number-input (expects + followed by digits). */
function toE164(value: string | undefined): string {
  if (value == null || value === '') return '';
  const digits = String(value).replace(/\D/g, '');
  return digits ? `+${digits}` : '';
}

const RenderField = <TFieldValues extends FieldValues>({
  field,
  props,
}: {
  field: any;
  props: CustomFormFieldProps<TFieldValues>;
}) => {
  const { placeholder, renderSkeleton, className, transform } = props;

  switch (props.filedType) {
    case FormFieldTypes.INPUT:
    case 'input':
      return (
        <FormControl>
          <Input
            placeholder={placeholder}
            {...field}
            type={props.type || 'text'}
            className={className}
            value={field.value ?? ''} // Ensure empty string for null/undefined
            onChange={e => {
              const value = e.target.value;
              if (props.type === 'number') {
                // Handle number input specifically
                const num = value === '' ? 0 : Number(value);
                field.onChange(isNaN(num) ? 0 : num);
              } else if (transform) {
                field.onChange(transform(value));
              } else {
                field.onChange(value);
              }
            }}
            onBlur={event => {
              field.onBlur?.(event);
              props.onBlur?.();
            }}
          />
        </FormControl>
      );

    case FormFieldTypes.TEXTAREA:
    case 'textarea':
      return (
        <FormControl>
          <Textarea
            placeholder={props.placeholder}
            {...field}
            className={className}
            disabled={props.disabled}
          />
        </FormControl>
      );

    case FormFieldTypes.PHONE_INPUT:
    case 'phone-input': {
      const phoneValue = field.value as string | undefined;
      const e164Value = toE164(phoneValue) || undefined;
      return (
        <FormControl>
          <PhoneInput
            defaultCountry="SA"
            placeholder={placeholder}
            international
            withCountryCallingCode
            value={(e164Value as E164Number) || undefined}
            onChange={field.onChange}
            disabled={props.disabled}
            className="input-phone"
            style={{ direction: 'ltr' }}
          />
        </FormControl>
      );
    }

    case FormFieldTypes.PHONE_OTP:
    case 'phone-otp':
      return (
        <FormControl>
          <Controller
            name={props.name}
            control={props.control}
            render={({ field }) => (
              <OtpInput
                value={field.value}
                onChange={otp => field.onChange(otp)}
                numInputs={5}
                shouldAutoFocus
                renderSeparator={<span className="mx-1 sm:mx-2"></span>}
                renderInput={inputProps => (
                  <input
                    {...inputProps}
                    placeholder="-"
                    className={`${inputProps.className} !w-[25%] sm:!w-[100%] mx-auto font-bold h-10 xs:h-14 sm:h-12 text-center border-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary`}
                  />
                )}
              />
            )}
          />
        </FormControl>
      );

    case FormFieldTypes.CHECKBOX:
    case 'checkbox':
      return (
        <FormControl>
          <div className="flex items-center justify-between">
            <label
              htmlFor={props.name}
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              {props.label}
            </label>
            <Checkbox
              id={props.name}
              checked={Array.isArray(field.value) && field.value.includes(props.value)}
              onCheckedChange={(checked: boolean) => {
                const currentValue = Array.isArray(field.value) ? field.value : [];
                const newValue = checked
                  ? [...currentValue, props.value]
                  : currentValue.filter((value: string) => value !== props.value);
                field.onChange(newValue);
              }}
            />
          </div>
        </FormControl>
      );
    case FormFieldTypes.DATE_PICKER:
    case 'date-picker':
      return (
        <FormControl>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={'outline'}
                className={cn(
                  'w-full justify-between text-start font-normal',
                  !field.value && 'text-muted-foreground',
                )}>
                {field.value ? format(field.value, 'PPP') : <span>{placeholder}</span>}
                <CalendarIcon className="w-4 h-4 me-2" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={field.value}
                onSelect={field.onChange}
                disabled={date => date < new Date('1900-01-01')}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </FormControl>
      );

    case FormFieldTypes.SELECT:
    case 'select': {
      const selectValue = field.value != null ? String(field.value) : props.value ?? '';
      return (
        <FormControl>
          <Select onValueChange={field.onChange} value={selectValue} disabled={props.disabled}>
            <FormControl>
              <SelectTrigger
                className={cn(
                  'text-start w-full bg-gray-100 border rounded-lg body1 px-3 py-2',
                  field.value ? 'text-foreground' : 'text-muted-foreground',
                )}
                disabled={props.disabled}>
                <SelectValue placeholder={props.placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>{props.children}</SelectContent>
          </Select>
        </FormControl>
      );
    }
    case FormFieldTypes.SKELETON:
    case 'skeleton':
      return renderSkeleton ? renderSkeleton(field) : null;
    default:
      return null;
  }
};

/** Renders InputLabel with form field id for accessibility. Must be used inside FormItem. */
function FormFieldInputLabel({ label, required }: { label: string; required?: boolean }) {
  const { formItemId } = useFormField();
  return <InputLabel label={label} required={required} htmlFor={formItemId} />;
}

export function CustomFormField<TFieldValues extends FieldValues = FieldValues>(
  props: CustomFormFieldProps<TFieldValues>,
) {
  const { control, name, filedType, label, required } = props;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          {filedType !== FormFieldTypes.CHECKBOX && label && (
            <FormFieldInputLabel label={label} required={required} />
          )}

          <RenderField field={field} props={props as CustomFormFieldProps<TFieldValues>} />

          {filedType !== FormFieldTypes.CHECKBOX && <FormMessage className="pt-0 text-error" />}
        </FormItem>
      )}
    />
  );
}
